import { PointerEvent as ReactPointerEvent, useCallback, useRef } from 'react';
import ChatIcon from '@material-ui/icons/Forum';
import CloseIcon from '@material-ui/icons/Close';
import { useKnowledgeBuilderColors } from '../../theme/knowledgeBuilderColors';

export const POSITION_STORAGE_KEY = 'rdap-global-assistant:position';
const DRAG_THRESHOLD_PX = 6;
export const BUTTON_SIZE_PX = 144;
export const MARGIN_PX = 16;

export interface Position {
  x: number;
  y: number;
}

export function defaultPosition(): Position {
  return {
    x: window.innerWidth - BUTTON_SIZE_PX - MARGIN_PX,
    y: window.innerHeight - BUTTON_SIZE_PX - MARGIN_PX,
  };
}

/**
 * Keeps a floating element inside the viewport.
 *
 * Width and height are optional so the same function can later be reused
 * by the expanded assistant window.
 */
export function clampPosition(
  position: Position,
  width = BUTTON_SIZE_PX,
  height = BUTTON_SIZE_PX,
): Position {
  const maxX = window.innerWidth - width - MARGIN_PX;
  const maxY = window.innerHeight - height - MARGIN_PX;

  return {
    x: Math.min(Math.max(position.x, MARGIN_PX), Math.max(maxX, MARGIN_PX)),
    y: Math.min(Math.max(position.y, MARGIN_PX), Math.max(maxY, MARGIN_PX)),
  };
}

export function loadPosition(): Position {
  try {
    const stored = localStorage.getItem(POSITION_STORAGE_KEY);

    if (stored) {
      return clampPosition(JSON.parse(stored) as Position);
    }
  } catch {
    // Fall through to default position.
  }

  return defaultPosition();
}

interface FloatingAssistantButtonProps {
  open: boolean;
  onToggle(): void;
  position: Position;
  onPositionChange(position: Position): void;
  iconUrl?: string;
}

interface DragState {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startPosition: Position;
  latestPosition: Position;
  moved: boolean;
}

export const FloatingAssistantButton = ({
  open,
  onToggle,
  position,
  onPositionChange,
  iconUrl,
}: FloatingAssistantButtonProps) => {
  const colors = useKnowledgeBuilderColors();

  const dragState = useRef<DragState>();

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      // Ignore non-primary mouse buttons.
      if (event.button !== 0) {
        return;
      }

      dragState.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPosition: position,
        latestPosition: position,
        moved: false,
      };

      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [position],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const drag = dragState.current;

      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - drag.startClientX;
      const deltaY = event.clientY - drag.startClientY;

      if (
        !drag.moved &&
        (Math.abs(deltaX) > DRAG_THRESHOLD_PX ||
          Math.abs(deltaY) > DRAG_THRESHOLD_PX)
      ) {
        drag.moved = true;
      }

      if (!drag.moved) {
        return;
      }

      const next = clampPosition({
        x: drag.startPosition.x + deltaX,
        y: drag.startPosition.y + deltaY,
      });

      drag.latestPosition = next;

      onPositionChange(next);
    },
    [onPositionChange],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const drag = dragState.current;

      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      dragState.current = undefined;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      if (drag.moved) {
        localStorage.setItem(
          POSITION_STORAGE_KEY,
          JSON.stringify(drag.latestPosition),
        );
      } else {
        onToggle();
      }
    },
    [onToggle],
  );

  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const drag = dragState.current;

      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      dragState.current = undefined;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [],
  );

  const transparentChrome = open || Boolean(iconUrl);

  return (
    <button
      type="button"
      aria-label={open ? 'Close assistant' : 'Open assistant'}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: BUTTON_SIZE_PX,
        height: BUTTON_SIZE_PX,
        zIndex: 1301,
        touchAction: 'none',
        border: 0,
        borderRadius: '50%',
        padding: 0,
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
        color: transparentChrome ? colors.text.primary : '#fff',
        background: transparentChrome ? 'transparent' : colors.accent.blue,
        boxShadow: transparentChrome ? 'none' : '0 6px 12px rgba(0, 0, 0, .24)',
      }}
      onClick={event => {
        if (event.detail === 0) onToggle();
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {open ? (
        <CloseIcon />
      ) : iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
      ) : (
        <ChatIcon />
      )}
    </button>
  );
};
